///
/// EarthBrowser
///
/// Created by Matt Giger on December 24, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { VectorTileId } from './VectorTileId'
import { MVTDecoder } from './MVTDecoder'
import { VectorTileCacheTable } from '@/earthbrowser/database/tables/VectorTileCacheTable'
import { Database } from '@/earthbrowser/database/Database'
import { logger } from '@/utils/LogDispatcher'
import type {
	VectorTileFetchOptions,
	VectorTileBatchFetchOptions,
	VectorTileSource,
	TileFetchResult,
	VectorTileManagerStats,
	VectorTileManagerOptions,
	DecodedVectorTile
} from './types'

/**
 * VectorTileManager - Manages MapLibre Vector Tiles (MVT)
 *
 * Features:
 * - Index and track available tiles
 * - Fetch tiles from URLs with retry logic
 * - Decode MVT Protocol Buffer format
 * - Cache tiles in SQLite for offline use
 * - Batch fetching with concurrency control
 * - LRU cache management
 * - Multiple source support
 */
export class VectorTileManager {
	private sources = new Map<string, VectorTileSource>()
	private options: Required<VectorTileManagerOptions>
	private pruneIntervalId?: number
	private metrics = {
		hits: 0,
		misses: 0,
		fetches: 0,
		errors: 0
	}
	private activeRequests = new Map<string, AbortController>()
	private tableInitialized = false

	constructor(options?: VectorTileManagerOptions) {
		this.options = {
			maxCacheSize: options?.maxCacheSize ?? 100 * 1024 * 1024, // 100MB
			defaultCacheTTL: options?.defaultCacheTTL ?? 7 * 24 * 60 * 60 * 1000, // 7 days
			autoPrune: options?.autoPrune ?? true,
			pruneInterval: options?.pruneInterval ?? 5 * 60 * 1000, // 5 minutes
			enableMetrics: options?.enableMetrics ?? true
		}

		this.initialize()
	}

	/**
	 * Initialize the manager
	 */
	private initialize(): void {
		this.ensureCacheTable()

		// Start auto-pruning if enabled
		if (this.options.autoPrune) {
			this.startAutoPrune()
		}
	}

	/**
	 * Create the cache table once the database is available. The singleton is
	 * often constructed at module import time, before the database opens, so
	 * this is retried lazily from the fetch/prune paths.
	 */
	private ensureCacheTable(): boolean {
		if (!this.tableInitialized && Database.getDb()) {
			VectorTileCacheTable.initialize(Database.db)
			this.tableInitialized = true
		}
		return this.tableInitialized
	}

	/**
	 * Register a tile source
	 */
	public addSource(source: VectorTileSource): void {
		this.sources.set(source.id, source)
		logger.log(`Registered vector tile source: ${source.name} (${source.id})`)
	}

	/**
	 * Remove a tile source
	 */
	public removeSource(sourceId: string): void {
		this.sources.delete(sourceId)
		VectorTileCacheTable.clearSource(sourceId)
		logger.log(`Removed vector tile source: ${sourceId}`)
	}

	/**
	 * Get a registered source
	 */
	public getSource(sourceId: string): VectorTileSource | undefined {
		return this.sources.get(sourceId)
	}

	/**
	 * Get all registered sources
	 */
	public getSources(): VectorTileSource[] {
		return Array.from(this.sources.values())
	}

	/**
	 * Fetch a single tile
	 */
	public async fetchTile(tileId: VectorTileId, options: VectorTileFetchOptions): Promise<TileFetchResult> {
		const requestKey = `${options.source}:${tileId.key}`

		try {
			// Check if already fetching
			if (this.activeRequests.has(requestKey)) {
				throw new Error(`Tile ${tileId.key} is already being fetched`)
			}

			// Check cache first unless force refresh. Cache errors must never
			// block the network fetch, so they only log
			const cacheAvailable = this.ensureCacheTable()
			if (!options.forceRefresh && cacheAvailable) {
				try {
					const cached = await VectorTileCacheTable.get(tileId, options.source)
					if (cached) {
						if (this.options.enableMetrics) this.metrics.hits++
						return {
							tileId,
							success: true,
							// Older cache entries may hold compressed payloads
							data: await this.gunzipIfNeeded(cached),
							fromCache: true,
							timestamp: Date.now()
						}
					}
				} catch (cacheError) {
					logger.warn(`Vector tile cache read failed for ${tileId.key}:`, cacheError)
				}
			}

			if (this.options.enableMetrics) this.metrics.misses++

			// Create abort controller for this request
			const abortController = options.signal
				? new AbortController()
				: new AbortController()

			if (options.signal) {
				options.signal.addEventListener('abort', () => abortController.abort())
			}

			this.activeRequests.set(requestKey, abortController)

			// Build URL from template
			const url = this.buildTileUrl(tileId, options.urlTemplate)

			// Fetch with retry logic
			const maxRetries = options.maxRetries ?? 3
			const retryDelay = options.retryDelay ?? 1000
			let lastError: Error | undefined

			for (let attempt = 0; attempt <= maxRetries; attempt++) {
				try {
					const raw = await this.fetchWithTimeout(url, {
						headers: options.headers,
						signal: abortController.signal
					}, 30000)

					// PMTiles archives store tiles gzipped. The browser only
					// decompresses when Content-Encoding is present, and local
					// wrangler dev (miniflare) rewrites that header to
					// mf-content-encoding, so sniff and decompress ourselves
					const data = await this.gunzipIfNeeded(raw)

					// Validate the data
					if (!MVTDecoder.isValidMVT(data)) {
						throw new Error('Invalid MVT data received')
					}

					// Cache the tile
					if (cacheAvailable) {
						const cacheTTL = options.cacheTTL ?? this.options.defaultCacheTTL
						const expires = Date.now() + cacheTTL
						VectorTileCacheTable.set(tileId, options.source, data, expires)
					}

					if (this.options.enableMetrics) this.metrics.fetches++

					return {
						tileId,
						success: true,
						data,
						fromCache: false,
						timestamp: Date.now()
					}
				} catch (error) {
					lastError = error instanceof Error ? error : new Error(String(error))

					if (abortController.signal.aborted) {
						throw new Error('Fetch aborted')
					}

					if (attempt < maxRetries) {
						await this.delay(retryDelay * (attempt + 1))
					}
				}
			}

			throw lastError || new Error('Failed to fetch tile')

		} catch (error) {
			if (this.options.enableMetrics) this.metrics.errors++

			const errorObj = error instanceof Error ? error : new Error(String(error))
			// warn (not error) so failed tile fetches don't become Sentry events
			logger.warn(`Failed to fetch tile ${tileId.key}:`, errorObj)

			return {
				tileId,
				success: false,
				error: errorObj,
				fromCache: false,
				timestamp: Date.now()
			}
		} finally {
			this.activeRequests.delete(requestKey)
		}
	}

	/**
	 * Fetch multiple tiles in batch
	 */
	public async fetchTiles(
		tileIds: VectorTileId[],
		options: VectorTileBatchFetchOptions
	): Promise<TileFetchResult[]> {
		const concurrency = options.concurrency ?? 6
		const results: TileFetchResult[] = []
		let completed = 0

		// Process tiles in batches
		for (let i = 0; i < tileIds.length; i += concurrency) {
			const batch = tileIds.slice(i, Math.min(i + concurrency, tileIds.length))

			const batchResults = await Promise.all(
				batch.map(tileId =>
					this.fetchTile(tileId, {
						source: options.source,
						urlTemplate: options.urlTemplate,
						cacheTTL: options.cacheTTL,
						headers: options.headers,
						signal: options.signal,
						maxRetries: options.maxRetries,
						retryDelay: options.retryDelay
					})
				)
			)

			results.push(...batchResults)
			completed += batchResults.length

			// Call progress callback
			if (options.onProgress) {
				options.onProgress(completed, tileIds.length)
			}

			// Call tile loaded callback
			if (options.onTileLoaded) {
				for (const result of batchResults) {
					options.onTileLoaded(result.tileId, result.success)
				}
			}
		}

		return results
	}

	/**
	 * Decode a tile from raw data
	 */
	public decodeTile(data: ArrayBuffer, tileId: VectorTileId): DecodedVectorTile {
		return MVTDecoder.decode(data, tileId)
	}

	/**
	 * Fetch and decode a tile in one operation
	 */
	public async fetchAndDecodeTile(
		tileId: VectorTileId,
		options: VectorTileFetchOptions
	): Promise<DecodedVectorTile | null> {
		const result = await this.fetchTile(tileId, options)

		if (result.success && result.data) {
			return this.decodeTile(result.data, tileId)
		}

		return null
	}

	/**
	 * Get tiles for a bounding box at a specific zoom level
	 */
	public getTilesForBounds(
		bounds: [number, number, number, number],
		zoom: number
	): VectorTileId[] {
		const [west, south, east, north] = bounds
		const tiles: VectorTileId[] = []

		// Get tile range
		const nwTile = VectorTileId.fromLngLat(west, north, zoom)
		const seTile = VectorTileId.fromLngLat(east, south, zoom)

		// Handle wrapping around the date line
		const minX = nwTile.x
		const maxX = seTile.x
		const minY = nwTile.y
		const maxY = seTile.y

		for (let y = minY; y <= maxY; y++) {
			if (minX <= maxX) {
				for (let x = minX; x <= maxX; x++) {
					tiles.push(new VectorTileId(zoom, x, y))
				}
			} else {
				// Handle wrapping
				const max = 1 << zoom
				for (let x = minX; x < max; x++) {
					tiles.push(new VectorTileId(zoom, x, y))
				}
				for (let x = 0; x <= maxX; x++) {
					tiles.push(new VectorTileId(zoom, x, y))
				}
			}
		}

		return tiles
	}

	/**
	 * Prefetch tiles for a bounding box
	 */
	public async prefetchBounds(
		bounds: [number, number, number, number],
		zoom: number,
		source: string
	): Promise<void> {
		const sourceConfig = this.sources.get(source)
		if (!sourceConfig) {
			throw new Error(`Source ${source} not found`)
		}

		const tiles = this.getTilesForBounds(bounds, zoom)

		await this.fetchTiles(tiles, {
			source,
			urlTemplate: sourceConfig.urlTemplate,
			cacheTTL: sourceConfig.cacheTTL,
			headers: sourceConfig.headers
		})
	}

	/**
	 * Get cache statistics
	 */
	public async getStats(source?: string): Promise<VectorTileManagerStats> {
		const dbStats = await VectorTileCacheTable.getStats(source)

		const stats: VectorTileManagerStats = {
			cachedTiles: dbStats.tileCount,
			cacheSize: dbStats.totalSize,
			averageTileSize: dbStats.avgTileSize,
			sources: source ? [source] : Array.from(this.sources.keys()),
			oldestTile: dbStats.oldestTile,
			newestTile: dbStats.newestTile
		}

		if (this.options.enableMetrics) {
			const total = this.metrics.hits + this.metrics.misses
			stats.hitRate = total > 0 ? this.metrics.hits / total : 0
			stats.missRate = total > 0 ? this.metrics.misses / total : 0
		}

		return stats
	}

	/**
	 * Clear cache for a specific source or all sources
	 */
	public clearCache(source?: string): void {
		if (source) {
			VectorTileCacheTable.clearSource(source)
		} else {
			VectorTileCacheTable.clear()
		}

		if (this.options.enableMetrics) {
			this.metrics = {
				hits: 0,
				misses: 0,
				fetches: 0,
				errors: 0
			}
		}
	}

	/**
	 * Manually trigger cache pruning
	 */
	public async pruneCache(): Promise<void> {
		if (!this.ensureCacheTable()) {
			return
		}
		try {
			VectorTileCacheTable.purgeExpired()
			await VectorTileCacheTable.pruneLRU(this.options.maxCacheSize)
		} catch (error) {
			logger.warn("Vector tile cache prune failed:", error)
		}
	}

	/**
	 * Cancel all active requests
	 */
	public cancelAllRequests(): void {
		for (const controller of this.activeRequests.values()) {
			controller.abort()
		}
		this.activeRequests.clear()
	}

	/**
	 * Clean up resources
	 */
	public destroy(): void {
		this.cancelAllRequests()
		this.stopAutoPrune()
		this.sources.clear()
	}

	// Private helper methods

	/**
	 * Decompress payloads that are still gzip (0x1f 0x8b magic). Loops because
	 * local wrangler dev can deliver double-gzipped bodies: the tile bytes are
	 * gzipped inside the PMTiles archive, and the dev server re-compresses the
	 * response without a Content-Encoding header the browser honors
	 */
	private async gunzipIfNeeded(data: ArrayBuffer): Promise<ArrayBuffer> {
		let result = data
		for (let pass = 0; pass < 4; pass++) {
			const bytes = new Uint8Array(result)
			if (bytes.length < 2 || bytes[0] !== 0x1f || bytes[1] !== 0x8b) {
				return result
			}

			const body = new Response(result).body
			if (!body) {
				return result
			}
			result = await new Response(body.pipeThrough(new DecompressionStream('gzip'))).arrayBuffer()
		}
		return result
	}

	private buildTileUrl(tileId: VectorTileId, template: string): string {
		return template
			.replace('{z}', tileId.z.toString())
			.replace('{x}', tileId.x.toString())
			.replace('{y}', tileId.y.toString())
			.replace('{quadkey}', tileId.quadkey)
	}

	private async fetchWithTimeout(
		url: string,
		options: { headers?: Record<string, string>, signal?: AbortSignal },
		timeoutMs: number
	): Promise<ArrayBuffer> {
		const controller = new AbortController()
		const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

		try {
			const response = await fetch(url, {
				headers: options.headers,
				signal: controller.signal
			})

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`)
			}

			return await response.arrayBuffer()
		} finally {
			clearTimeout(timeoutId)
		}
	}

	private delay(ms: number): Promise<void> {
		return new Promise(resolve => setTimeout(resolve, ms))
	}

	private startAutoPrune(): void {
		this.pruneIntervalId = window.setInterval(() => {
			void this.pruneCache()
		}, this.options.pruneInterval)
	}

	private stopAutoPrune(): void {
		if (this.pruneIntervalId !== undefined) {
			clearInterval(this.pruneIntervalId)
			this.pruneIntervalId = undefined
		}
	}
}

/**
 * Global instance for easy access
 */
export const vectorTileManager = new VectorTileManager()

